import React from 'react'
import { Pressable, ScrollView, View } from 'react-native'

import { Network } from '@ambire-common/interfaces/network'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LeftArrowIcon from '@common/assets/svg/LeftArrowIcon'
import BottomSheet from '@common/components/BottomSheet'
import BaseAddress from '@common/components/HumanizerAddress/components/BaseAddress'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import spacings from '@common/styles/spacings'
import { setStringAsync } from '@common/utils/clipboard'
import BalanceChangesSection from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/BalanceChangesSection'
import FinalStatusRow from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/FinalStatusRow'
import PrivateSummaryFooter from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/PrivateSummaryFooter'
import { getTruncatedTxnHash } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/helpers'
import getStyles from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/styles'
import { DisplayBalanceChange } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/types'
import TransactionSummary, {
  sizeMultiplier
} from '@web/modules/sign-account-op/components/TransactionSummary'

import type { Modalize } from 'react-native-modalize'

// Private counterpart of the public SummaryDetailsSheet. The public detail view
// (SummaryDetails) is built around an AccountOp shape (humanized calls, balance
// changes, identifiedBy, delegation meta), none of which exist for a RAILGUN
// TransactionHistoryItem — so we render the same sheet chrome and the same
// section components (BalanceChangesSection / FinalStatusRow / the Open-explorer
// footer) here, fed the already-computed private fields from PrivateActivityRow.
// Keeping the two sheets on one visual format is Trello #467.
type Props = {
  sheetRef: React.RefObject<Modalize>
  closeBottomSheet: () => void
  modalType: 'modal' | 'bottom-sheet'
  network: Network
  size: 'sm' | 'md' | 'lg'
  call: any
  chainId: bigint
  txid: string
  isPrivateCounterparty: boolean
  isEvmAddress: boolean
  counterpartyAddress: string
  extraMessage?: string | null
  status: AccountOpStatus
  /** Assets in/out of this private txn, already resolved by PrivateActivityRow. */
  balanceChanges?: DisplayBalanceChange[]
  /** Preformatted network fee (e.g. "0.000051 BNB"), or undefined when unknown. */
  feeText?: string
  isFeeLoading: boolean
  timestamp: number
  isBlocked?: boolean
  ppoiUrl?: string
  onUnshieldToOrigin?: () => void
}

const PrivateSummaryDetailsSheet = ({
  sheetRef,
  closeBottomSheet,
  modalType,
  network,
  size,
  call,
  chainId,
  txid,
  isPrivateCounterparty,
  isEvmAddress,
  counterpartyAddress,
  extraMessage,
  status,
  balanceChanges,
  feeText,
  isFeeLoading,
  timestamp,
  isBlocked,
  ppoiUrl,
  onUnshieldToOrigin
}: Props) => {
  const { styles, theme } = useTheme(getStyles)
  const { t } = useTranslation()
  const { addToast } = useToast()

  const isPendingConfirmation =
    status === AccountOpStatus.Pending || status === AccountOpStatus.BroadcastedButNotConfirmed

  return (
    <BottomSheet
      sheetRef={sheetRef}
      closeBottomSheet={closeBottomSheet}
      type={modalType}
      adjustToContentHeight={modalType === 'modal'}
      containerInnerWrapperStyles={styles.sheetContainer}
      style={{
        maxWidth: 720,
        paddingVertical: 0,
        paddingHorizontal: 0,
        overflow: 'hidden'
      }}
    >
      <View style={styles.sheetContainer}>
        <View style={styles.sheetHeader}>
          <Pressable
            onPress={() => {
              closeBottomSheet()
            }}
            style={styles.sheetHeaderBackButton}
          >
            <LeftArrowIcon color={theme.secondaryText} />
          </Pressable>
          <Text weight="semiBold" style={styles.sheetHeaderTitle}>
            {t('Activity information')}
          </Text>
          <View style={styles.sheetHeaderBackButton} />
        </View>
        <ScrollView
          bounces={false}
          showsVerticalScrollIndicator={false}
          style={styles.sheetScroll}
          contentContainerStyle={styles.sheetScrollContent}
        >
          {/* Same double phSm padding as the public SummaryDetails. */}
          <View style={spacings.phSm}>
            <View style={spacings.phSm}>
              <View style={styles.modalSection} testID="activity-transaction-details-step">
                <Text appearance="tertiaryText" weight="medium" style={spacings.mbSm}>
                  {t('Transaction details')}
                </Text>
                <TransactionSummary
                  style={styles.summaryItem}
                  call={call}
                  chainId={chainId}
                  isHistory
                  enableExpand
                  size={size}
                  hideLinks
                />
                {!isPrivateCounterparty && !isEvmAddress && (
                  <View
                    style={{
                      paddingHorizontal: 18.2,
                      paddingBottom: 6.8,
                      marginTop: -6.8
                    }}
                  >
                    <BaseAddress address={counterpartyAddress} chainId={chainId} hideLinks>
                      {shortenAddress(counterpartyAddress, 16)}
                    </BaseAddress>
                  </View>
                )}
                {!!extraMessage && (
                  <View style={[spacings.phMd, spacings.pbMi]}>
                    <Text
                      fontSize={12 * sizeMultiplier[size]}
                      appearance="secondaryText"
                      weight="medium"
                      numberOfLines={6}
                    >
                      {extraMessage}
                    </Text>
                  </View>
                )}
              </View>
              <BalanceChangesSection
                changes={balanceChanges || []}
                isLoading={!balanceChanges?.length && isPendingConfirmation}
              />
              {!!txid && (
                <View
                  style={[styles.modalConfirmedRow, spacings.mbSm]}
                  testID="activity-transaction-hash-step"
                >
                  <View style={styles.modalStepRow}>
                    <Text appearance="tertiaryText" weight="medium">
                      {t('Transaction hash')}
                    </Text>
                    <View style={styles.modalStepRowRight}>
                      <Text fontSize={14} appearance="secondaryText">
                        {getTruncatedTxnHash(txid)}
                      </Text>
                      <Pressable
                        onPress={() => {
                          void setStringAsync(txid)
                          addToast(t('Copied to clipboard!') as string, { timeout: 2500 })
                        }}
                        style={styles.modalHashCopyButton}
                      >
                        <CopyIcon width={16} height={16} color={theme.primaryText} />
                      </Pressable>
                    </View>
                  </View>
                </View>
              )}
              {/* The public sheet has no fee row (the fee is part of its balance
                  changes); a private txn pays its fee publicly, so surface it as
                  its own row in the SAME row format as "Transaction hash". */}
              {(isFeeLoading || !!feeText) && (
                <View
                  style={[styles.modalConfirmedRow, spacings.mbSm]}
                  testID="activity-transaction-fee-step"
                >
                  <View style={styles.modalStepRow}>
                    <Text appearance="tertiaryText" weight="medium">
                      {t('Fee')}
                    </Text>
                    {isFeeLoading ? (
                      <SkeletonLoader width={80} height={21} appearance="tertiaryBackground" />
                    ) : (
                      <Text fontSize={14} appearance="secondaryText">
                        {feeText}
                      </Text>
                    )}
                  </View>
                </View>
              )}
              <FinalStatusRow
                status={status}
                chainId={chainId}
                networkName={network.name}
                timestamp={timestamp}
                isBlocked={isBlocked}
              />
            </View>
          </View>
        </ScrollView>
        <PrivateSummaryFooter
          network={network}
          txnId={txid}
          isBlocked={isBlocked}
          ppoiUrl={ppoiUrl}
          onUnshieldToOrigin={onUnshieldToOrigin}
        />
      </View>
    </BottomSheet>
  )
}

export default React.memo(PrivateSummaryDetailsSheet)
